/**
 * KI-Verwaltung (Spec 9.1 / 9.4): Einstellungen je Mandant oben, Wissensbasis
 * darunter.
 *
 * **Hier wird kein Schluessel mehr eingetippt** (ADR 0017). Provider, Modell
 * und die beiden API-Schluessel liegen in einer `ai_provider`-Verbindung, die
 * der Betreiber unter „Plattform → Verbindungen" pflegt und fuer diesen
 * Mandanten freigibt. Der Mandant waehlt nur noch *welche* — und entscheidet
 * ueber die beiden Dinge, die ihm gehoeren: an/aus und die Tageslimits.
 *
 * Ohne gewaehlte Verbindung gilt der Mock: deterministisch, kostenlos, kein
 * Aufruf nach draussen.
 */
import type {
  AiSettings,
  ConnectionRef,
  KbArticle,
  UpdateAiSettingsInput,
} from '@tickethub/shared';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { aiApi, connectionsApi } from '@/lib/tickets';

export function AiPage() {
  const { t } = useTranslation();
  const { activeMembership, reload } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;
  const isAdmin = activeMembership?.role === 'admin';

  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [form, setForm] = useState<UpdateAiSettingsInput>({});
  const [connections, setConnections] = useState<ConnectionRef[]>([]);
  const [saving, setSaving] = useState(false);

  const [articles, setArticles] = useState<KbArticle[] | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      if (isAdmin) {
        try {
          setSettings(await aiApi.getSettings(tenantId));
          setConnections((await connectionsApi.list(tenantId, 'ai_provider')).items);
        } catch (error) {
          toastError(error, t);
        }
      }
      try {
        setArticles((await aiApi.listKb(tenantId)).items);
      } catch (error) {
        setArticles([]);
        toastError(error, t);
      }
    })();
  }, [tenantId, isAdmin, t]);

  async function save() {
    if (!tenantId) return;
    setSaving(true);
    try {
      setSettings(await aiApi.saveSettings(tenantId, form));
      setForm({});
      // `ai_enabled` haengt an der Session — sonst bliebe die KI-Oberflaeche
      // am Ticket bis zum naechsten Login unsichtbar bzw. sichtbar.
      await reload();
    } catch (error) {
      toastError(error, t);
    } finally {
      setSaving(false);
    }
  }

  async function reloadArticles() {
    if (!tenantId) return;
    setArticles((await aiApi.listKb(tenantId)).items);
  }

  async function createArticle() {
    if (!tenantId || title.trim().length === 0 || body.trim().length === 0) return;
    setCreating(true);
    try {
      await aiApi.createKb(tenantId, {
        title: title.trim(),
        body_text: body.trim(),
        origin: 'manual',
        source_ticket_id: null,
        active: true,
      });
      setTitle('');
      setBody('');
      await reloadArticles();
    } catch (error) {
      toastError(error, t);
    } finally {
      setCreating(false);
    }
  }

  async function toggleArticle(article: KbArticle) {
    if (!tenantId) return;
    try {
      await aiApi.updateKb(tenantId, article.id, { active: !article.active });
      await reloadArticles();
    } catch (error) {
      toastError(error, t);
    }
  }

  async function removeArticle(article: KbArticle) {
    if (!tenantId) return;
    try {
      await aiApi.removeKb(tenantId, article.id);
      await reloadArticles();
    } catch (error) {
      toastError(error, t);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">{t('ai.page_title')}</h1>

      {isAdmin && settings ? (
        <section className="space-y-4 rounded-lg border p-4">
          <h2 className="text-lg font-medium">{t('ai.settings_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('ai.settings_hint')}</p>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled ?? settings.enabled}
              onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            />
            {t('ai.settings_enabled')}
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ai-connection">{t('ai.settings_connection')}</Label>
              <Select
                id="ai-connection"
                value={form.connection_id ?? settings.connection_id ?? ''}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    connection_id: event.target.value === '' ? null : event.target.value,
                  }))
                }
              >
                <option value="">{t('ai.settings_connection_none')}</option>
                {connections.map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('ai.settings_connection_hint', {
                  provider: settings.provider,
                  model: settings.model,
                })}
              </p>
              {connections.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('ai.settings_no_connections')}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-call-limit">{t('ai.settings_call_limit')}</Label>
              <Input
                id="ai-call-limit"
                type="number"
                min={0}
                value={form.daily_call_limit ?? settings.daily_call_limit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    daily_call_limit: Number(event.target.value),
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-token-limit">{t('ai.settings_token_limit')}</Label>
              <Input
                id="ai-token-limit"
                type="number"
                min={0}
                value={form.daily_token_limit ?? settings.daily_token_limit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    daily_token_limit: Number(event.target.value),
                  }))
                }
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('ai.settings_usage', {
              calls: settings.usage.calls,
              tokens: settings.usage.tokens,
              day: settings.usage.day,
            })}
          </p>

          {!settings.embeddings_available ? (
            <Alert>
              <AlertDescription>{t('ai.settings_no_embeddings')}</AlertDescription>
            </Alert>
          ) : null}

          <Button disabled={saving} onClick={() => void save()}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </section>
      ) : null}

      <section className="space-y-4 rounded-lg border p-4">
        <h2 className="text-lg font-medium">{t('ai.kb_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('ai.kb_hint')}</p>

        <div className="grid gap-3">
          <div className="space-y-2">
            <Label htmlFor="kb-title">{t('ai.kb_article_title')}</Label>
            <Input id="kb-title" value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kb-body">{t('ai.kb_article_body')}</Label>
            <textarea
              id="kb-body"
              className="min-h-32 w-full rounded-md border border-input bg-background p-2 text-sm"
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </div>
          <Button
            className="justify-self-start"
            disabled={creating || title.trim().length === 0 || body.trim().length === 0}
            onClick={() => void createArticle()}
          >
            {creating ? t('common.loading') : t('ai.kb_create')}
          </Button>
        </div>

        {articles === null ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : articles.length === 0 ? (
          <Alert>
            <AlertDescription>{t('ai.kb_empty')}</AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {articles.map((article) => (
              <li key={article.id} className="flex flex-wrap items-start gap-3 rounded-lg border p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{article.title}</p>
                  <p className="truncate text-sm text-muted-foreground">{article.body_text}</p>
                </div>
                <Badge variant="outline">{t(`ai.kb_origin_${article.origin}`)}</Badge>
                {!article.has_embedding ? (
                  <Badge variant="secondary">{t('ai.kb_no_embedding')}</Badge>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => void toggleArticle(article)}>
                  {article.active ? t('ai.kb_deactivate') : t('ai.kb_activate')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('ai.kb_delete', { title: article.title })}
                  onClick={() => void removeArticle(article)}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
